import { supabase } from '../../config/supabase';

/**
 * Get or create the "Notify Team" system user for a company
 */
const getOrCreateNotifyTeamUser = async (companyId) => {
    try {
        // Check if Notify Team user already exists for this company
        const { data: existingUser, error: fetchError } = await supabase
            .from('users')
            .select('id')
            .eq('company_id', companyId)
            .eq('username', 'Notify Team')
            .eq('role', 'system')
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
            throw fetchError;
        }

        if (existingUser) {
            return existingUser.id;
        }

        // Create Notify Team system user
        const { data: newUser, error: insertError } = await supabase
            .from('users')
            .insert({
                company_id: companyId,
                username: 'Notify Team',
                full_name: 'Notify Team',
                email: `info@notifyapp.com.au`,
                role: 'system',
                status: 'active'
            })
            .select('id')
            .single();

        if (insertError) throw insertError;

        return newUser.id;
    } catch (error) {
        console.error('Error getting/creating Notify Team user:', error);
        throw error;
    }
};

/**
 * Get or create a conversation between Notify Team and a user
 */
const getOrCreateNotifyTeamConversation = async (companyId, recipientId, notifyTeamUserId) => {
    try {
        // Check if conversation already exists between Notify Team and recipient
        const { data: existingConv, error: fetchError } = await supabase
            .from('conversations')
            .select('id')
            .eq('company_id', companyId)
            .or(`and(participant1_id.eq.${notifyTeamUserId},participant2_id.eq.${recipientId}),and(participant1_id.eq.${recipientId},participant2_id.eq.${notifyTeamUserId})`)
            .single();

        if (fetchError && fetchError.code !== 'PGRST116') {
            throw fetchError;
        }

        if (existingConv) {
            return existingConv.id;
        }

        // Create new conversation
        const { data: newConv, error: insertError } = await supabase
            .from('conversations')
            .insert({
                participant1_id: notifyTeamUserId,
                participant2_id: recipientId,
                company_id: companyId
            })
            .select()
            .single();

        if (insertError) throw insertError;

        return newConv.id;
    } catch (error) {
        console.error('Error getting/creating conversation:', error);
        throw error;
    }
};

/**
 * Send a message from Notify Team to multiple recipients
 */
export const sendNotifyTeamMessage = async (companyId, recipientIds, messageContent) => {
    try {
        // Get or create the Notify Team system user
        const notifyTeamUserId = await getOrCreateNotifyTeamUser(companyId);

        const results = [];

        for (const recipientId of recipientIds) {
            // Get or create conversation between Notify Team and recipient
            const conversationId = await getOrCreateNotifyTeamConversation(
                companyId,
                recipientId,
                notifyTeamUserId
            );

            // Send message from Notify Team
            const { data: message, error: msgError } = await supabase
                .from('messages')
                .insert({
                    conversation_id: conversationId,
                    sender_id: notifyTeamUserId,
                    receiver_id: recipientId,
                    content: messageContent.trim(),
                    is_read: false
                })
                .select()
                .single();

            if (msgError) throw msgError;

            results.push({ recipientId, conversationId, messageId: message.id });
        }

        return {
            success: true,
            count: results.length,
            results
        };
    } catch (error) {
        console.error('Error sending Notify Team messages:', error);
        throw error;
    }
};

/**
 * Send notifications to multiple recipients
 */
export const sendBulkNotifications = async (recipientIds, notificationData) => {
    try {
        const { title, message, type, priority } = notificationData;

        // Create notification objects for all recipients
        const notifications = recipientIds.map(recipientId => ({
            user_id: recipientId,
            title: title.trim(),
            message: message.trim(),
            type: type || 'system_alert',
            priority: priority || 'normal',
            is_read: false
        }));

        // Insert all notifications at once
        const { data, error } = await supabase
            .from('notifications')
            .insert(notifications)
            .select();

        if (error) throw error;

        return {
            success: true,
            count: data.length,
            notifications: data
        };
    } catch (error) {
        console.error('Error sending bulk notifications:', error);
        throw error;
    }
};

/**
 * Get recipients based on selection type
 */
export const getRecipientIds = (recipientType, selectedUsers, selectedRole, allUsers) => {
    let recipientIds = [];

    switch (recipientType) {
        case 'single':
            recipientIds = selectedUsers.length > 0 ? selectedUsers : [];
            break;
        case 'role':
            recipientIds = selectedRole
                ? allUsers.filter(u => u.role === selectedRole).map(u => u.id)
                : [];
            break;
        case 'everyone':
            recipientIds = allUsers.map(u => u.id);
            break;
        case 'custom':
            recipientIds = selectedUsers.length > 0 ? selectedUsers : [];
            break;
        default:
            recipientIds = [];
    }

    return recipientIds;
};
